import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import {
  uploadFirstHeaderImage,
  uploadSecondHeaderImage,
} from "./../../../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  const base = String(BASE_URL).replace(/\/$/, "");
  const path = String(url).startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
};

export default function Accueil({
  settings,
  setSettings,
  handleChange,
  galleries = [],
  disabled = false,
}) {
  const [uploading, setUploading] = useState(false);

  const handleQuillChange = (value, name) => {
    handleChange({ target: { name, value } });
  };

  const removeImage = (key) => {
    if (disabled) {
      toast.info("Action désactivée.");
      return;
    }
    if (!window.confirm("Supprimer cette image des paramètres ?")) return;

    setSettings((prev) => ({ ...prev, [key]: "" }));
    toast.success("Image retirée ✅ Pense à enregistrer.");
  };

  const handleFirstImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (disabled) {
      toast.info("Action désactivée.");
      return;
    }

    const formData = new FormData();
    formData.append("firstHeaderImage", file);
    formData.append("category", "settings");

    setUploading(true);
    try {
      const uploadResponse = await uploadFirstHeaderImage(formData);
      if (!uploadResponse?.filePath) throw new Error("Échec upload");
      setSettings((prev) => ({ ...prev, header1_photo_url: uploadResponse.filePath }));
      toast.success("Image 1 téléversée ✅ Pense à enregistrer.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur upload image 1.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleSecondImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (disabled) {
      toast.info("Action désactivée.");
      return;
    }

    const formData = new FormData();
    formData.append("secondHeaderImage", file);
    formData.append("category", "settings");

    setUploading(true);
    try {
      const uploadResponse = await uploadSecondHeaderImage(formData);
      if (!uploadResponse?.filePath) throw new Error("Échec upload");
      setSettings((prev) => ({ ...prev, header2_photo_url: uploadResponse.filePath }));
      toast.success("Image 2 téléversée ✅ Pense à enregistrer.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur upload image 2.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const inputClass =
    "w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";
  const labelClass = "block text-xs font-extrabold uppercase text-gray-600 dark:text-gray-300";

  const galleryLabel = (g) =>
    g?.titre_fr || g?.title_fr || g?.titre || g?.title || `Galerie #${g?.id ?? ""}`;

  return (
    <div className="space-y-5">
      {/* Images */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Image 1 */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="font-extrabold text-gray-900 dark:text-gray-100">
              Image header 1
            </div>

            {settings.header1_photo_url ? (
              <button
                type="button"
                onClick={() => removeImage("header1_photo_url")}
                disabled={disabled || uploading}
                className="px-3 py-2 rounded-xl text-xs font-extrabold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                Supprimer
              </button>
            ) : null}
          </div>

          {settings.header1_photo_url ? (
            <img
              src={toAbs(settings.header1_photo_url)}
              alt="Header 1"
              className="w-full max-h-48 object-cover rounded-xl mt-3"
            />
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-3">
              Aucune image
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={handleFirstImageChange}
            disabled={disabled || uploading}
            className="block w-full text-sm mt-3"
          />
        </div>

        {/* Image 2 */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="font-extrabold text-gray-900 dark:text-gray-100">
              Image header 2
            </div>

            {settings.header2_photo_url ? (
              <button
                type="button"
                onClick={() => removeImage("header2_photo_url")}
                disabled={disabled || uploading}
                className="px-3 py-2 rounded-xl text-xs font-extrabold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                Supprimer
              </button>
            ) : null}
          </div>

          {settings.header2_photo_url ? (
            <img
              src={toAbs(settings.header2_photo_url)}
              alt="Header 2"
              className="w-full max-h-48 object-cover rounded-xl mt-3"
            />
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-3">
              Aucune image
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={handleSecondImageChange}
            disabled={disabled || uploading}
            className="block w-full text-sm mt-3"
          />
        </div>
      </div>

      {/* Textes header */}
      <div>
        <label className={labelClass}>Titre header (FR)</label>
        <ReactQuill
          value={settings.header_title_fr || ""}
          onChange={(v) => handleQuillChange(v, "header_title_fr")}
          className="bg-white dark:bg-gray-700 rounded-xl"
        />
      </div>

      <div>
        <label className={labelClass}>Titre header (EN)</label>
        <ReactQuill
          value={settings.header_title_en || ""}
          onChange={(v) => handleQuillChange(v, "header_title_en")}
          className="bg-white dark:bg-gray-700 rounded-xl"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass}>Sous-titre (FR)</label>
          <input
            type="text"
            name="header_subtitle_fr"
            value={settings.header_subtitle_fr || ""}
            onChange={handleChange}
            disabled={disabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Sous-titre (EN)</label>
          <input
            type="text"
            name="header_subtitle_en"
            value={settings.header_subtitle_en || ""}
            onChange={handleChange}
            disabled={disabled}
            className={inputClass}
          />
        </div>
      </div>

      {/* À propos */}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass}>Titre À propos (FR)</label>
          <input
            type="text"
            name="about_title_fr"
            value={settings.about_title_fr || ""}
            onChange={handleChange}
            disabled={disabled}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Titre À propos (EN)</label>
          <input
            type="text"
            name="about_title_en"
            value={settings.about_title_en || ""}
            onChange={handleChange}
            disabled={disabled}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Contenu À propos (FR)</label>
        <ReactQuill
          value={settings.about_content_fr || ""}
          onChange={(v) => handleQuillChange(v, "about_content_fr")}
          className="bg-white dark:bg-gray-700 rounded-xl"
        />
      </div>

      <div>
        <label className={labelClass}>Contenu À propos (EN)</label>
        <ReactQuill
          value={settings.about_content_en || ""}
          onChange={(v) => handleQuillChange(v, "about_content_en")}
          className="bg-white dark:bg-gray-700 rounded-xl"
        />
      </div>

      {/* Carrousel */}
      <div>
        <label className={labelClass}>Galerie du carrousel</label>
        <select
          name="carrousel_id"
          value={settings.carrousel_id || ""}
          onChange={handleChange}
          disabled={disabled}
          className={inputClass}
        >
          <option value="">-- Sélectionner une galerie --</option>
          {galleries.map((g) => (
            <option key={g.id} value={g.id}>
              {galleryLabel(g)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
